import { useEffect, useMemo, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { initialHash, supabase } from "./supabase";
import { listSales, loadConfig } from "./data";
import { AppContext } from "./app-context";
import { href, useRoute, type Route } from "./router";
import { ErrorNote, Loading, useLoad } from "./ui";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { CompaniesPage } from "./pages/Companies";

export function App() {
  // undefined = still reading any session from storage or the OAuth redirect.
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [authError] = useState(() => oauthErrorFromUrl());

  useEffect(() => {
    let live = true;
    supabase.auth.getSession().then(({ data, error }) => {
      if (!live) return;
      if (error) console.error("getSession failed", error);
      setSession(data.session);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => { live = false; data.subscription.unsubscribe(); };
  }, []);

  if (session === undefined) return <main className="center"><Loading /></main>;
  if (!session) return <Login error={authError} />;
  return <SignedIn userId={session.user.id} />;
}

/** GoTrue sends a failed OAuth sign-in back with #error=…&error_description=…. */
function oauthErrorFromUrl(): string | null {
  const params = new URLSearchParams(initialHash.replace(/^#/, ""));
  const description = params.get("error_description") ?? params.get("error");
  if (!description) return null;
  if (window.location.hash.includes("error")) history.replaceState(null, "", window.location.pathname);
  return description.replace(/\+/g, " ");
}

function SignedIn({ userId }: { userId: string }) {
  const route = useRoute();
  const base = useLoad(async () => {
    const [config, sales] = await Promise.all([loadConfig(), listSales()]);
    return { config, sales };
  }, [userId]);

  const app = useMemo(() => {
    if (!base.data) return null;
    const { config, sales } = base.data;
    return { config, sales, me: sales.find((s) => s.user_id === userId), salesById: new Map(sales.map((s) => [s.id, s])) };
  }, [base.data, userId]);

  useEffect(() => { if (app) document.title = `${app.config.title} · CRM`; }, [app]);

  if (base.error) return <main className="center"><ErrorNote error={base.error} /></main>;
  if (!app) return <main className="center"><Loading /></main>;

  return (
    <AppContext.Provider value={app}>
      <div className="shell">
        <Nav route={route} title={app.config.title} />
        <main className="content">
          <Page route={route} />
        </main>
      </div>
    </AppContext.Provider>
  );
}

function Nav({ route, title }: { route: Route; title: string }) {
  const current = (...pages: Route["page"][]) => (pages.includes(route.page) ? "page" : undefined);
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) console.error("sign-out failed", error);
  };
  return (
    <nav className="nav" aria-label="Main">
      <a className="brand" href={href({ page: "dashboard" })}>
        <img src="/favicon.png" alt="" width={22} height={22} />
        <span>{title}</span>
      </a>
      <a href={href({ page: "dashboard" })} aria-current={current("dashboard")}>Dashboard</a>
      <a href={href({ page: "companies" })} aria-current={current("companies", "person")}>Companies</a>
      <span className="spacer" />
      <button type="button" className="nav-btn" onClick={signOut}>Sign out</button>
    </nav>
  );
}

function Page({ route }: { route: Route }) {
  switch (route.page) {
    case "dashboard": return <Dashboard />;
    case "companies":
    case "person": return <CompaniesPage route={route} />;
  }
}
